import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { defer, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import { PublicSettings } from '../../_models/admin/app-setting.models';
import { ACTION_OVERDUE_DAYS } from '../../_constants/action-rules';
import { SESSION_DATE_PICKER_FORWARD_DAYS, SESSION_LAUNCH_WINDOW_MINUTES } from '../../_constants/session-rules';

/**
 * The settings any signed-in user may read (GET api/Settings/Public), fetched once per app load and
 * shared. Every value falls back to its compiled-in constant when the call fails, so a screen never
 * waits on, or breaks over, this read.
 */
@Injectable({ providedIn: 'root' })
export class PublicSettingsService extends BaseService {

  private apiUrl: string = environment.apiUrl;
  private settings$: Observable<PublicSettings | null> | null = null;

  constructor(private http: HttpClient, protected hashManagerService: SessionUserHashManagerService) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  /** Days before an open action counts as overdue: the served value, else ACTION_OVERDUE_DAYS. */
  public actionsOverdueDays(): Observable<number> {
    return this.getSettings().pipe(map(s => {
      const days = s ? Number(s.actionsOverdueDays) : NaN;
      return Number.isInteger(days) && days > 0 ? days : ACTION_OVERDUE_DAYS;
    }));
  }

  /**
   * The safeguarding contact for a booking's brand (companyId), resolved on the server as the brand's
   * override, else the global value. Null when none is configured or the call fails, so a caller leaves the
   * contact out rather than showing a placeholder.
   */
  public safeguardingEmailFor(companyId: number | null | undefined): Observable<string | null> {
    return this.getSettings().pipe(map(s => {
      if (!s) {
        return null;
      }
      const perBrand = s.safeguardingEmails || {};
      const key = companyId != null ? String(companyId) : null;
      const email = key != null && Object.prototype.hasOwnProperty.call(perBrand, key) ? perBrand[key] : s.safeguardingEmail;
      return email && email.trim() ? email.trim() : null;
    }));
  }

  /** Minutes either side of a session in which it can be launched: the served value, else SESSION_LAUNCH_WINDOW_MINUTES. */
  public sessionLaunchWindowMinutes(): Observable<number> {
    return this.getSettings().pipe(map(s => PublicSettingsService.positiveInt(s?.sessionLaunchWindowMinutes, SESSION_LAUNCH_WINDOW_MINUTES)));
  }

  /** The session date picker's latest date in days from today: the served value, else SESSION_DATE_PICKER_FORWARD_DAYS. */
  public sessionDatePickerForwardDays(): Observable<number> {
    return this.getSettings().pipe(map(s => PublicSettingsService.positiveInt(s?.sessionDatePickerForwardDays, SESSION_DATE_PICKER_FORWARD_DAYS)));
  }

  private static positiveInt(value: unknown, fallback: number): number {
    const n = Number(value);
    return Number.isInteger(n) && n > 0 ? n : fallback;
  }

  private getSettings(): Observable<PublicSettings | null> {
    if (!this.settings$) {
      this.settings$ = defer(() => this.http.get(this.apiUrl + 'api/Settings/Public', this.prepareOptions(null))).pipe(
        map((data: any) => (data ? data.body : data) as PublicSettings),
        catchError(() => of(null)),
        shareReplay(1)
      );
    }
    return this.settings$;
  }
}
